import { useMutation } from '@tanstack/react-query';
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AssistantTurn {
  conversation_id: string;
  role: string;
  model: string;
  answer: string;
  sources: number;
}

interface SendArgs {
  message: string;
  conversationId?: string;
  model?: string;
}

/**
 * Unwrap the body of a Supabase Functions error so the user (and the
 * developer) sees the real reason instead of the generic
 * "Edge Function returned a non-2xx status code" string.
 *
 * Common cases this surfaces:
 *  - 404 "Function not found" → the `ai-assistant` function was never deployed
 *  - 500 "ANTHROPIC_API_KEY not configured" → secret not set on the project
 *  - 502 "anthropic chat failed: 404 …" → invalid model id, etc.
 */
async function unwrapFunctionsError(err: unknown): Promise<Error> {
  if (err instanceof FunctionsHttpError) {
    const res = err.context as Response;
    const status = res.status;
    try {
      const cloned = res.clone();
      const ct = cloned.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        const body = (await cloned.json()) as { error?: string; message?: string };
        const detail = body?.error ?? body?.message;
        if (detail) return new Error(`${detail} (HTTP ${status})`);
      } else {
        const text = (await cloned.text()).trim();
        if (text) return new Error(`${text.slice(0, 300)} (HTTP ${status})`);
      }
    } catch {
      // fall through to the generic message
    }
    return new Error(`Edge Function returned HTTP ${status}`);
  }
  if (err instanceof FunctionsRelayError) {
    return new Error(`Supabase relay error: ${err.message}`);
  }
  if (err instanceof FunctionsFetchError) {
    return new Error(`Network error reaching Atlas: ${err.message}`);
  }
  if (err instanceof Error) return err;
  return new Error('Unknown assistant error');
}

/**
 * Calls the `ai-assistant` Edge Function. The function uses the caller's JWT
 * so RLS on conversations/messages stays role-aware. The optional `model` is
 * validated server-side against an allowlist; unknown values fall back to the
 * server default.
 */
export function useAssistantSend() {
  return useMutation<AssistantTurn, Error, SendArgs>({
    mutationFn: async ({ message, conversationId, model }) => {
      const { data, error } = await supabase.functions.invoke<AssistantTurn>('ai-assistant', {
        body: { message, conversation_id: conversationId, model },
      });
      if (error) throw await unwrapFunctionsError(error);
      if (!data) throw new Error('No response from assistant');
      return data;
    },
  });
}
